"use client";

/**
 * FullAnalysisResults — renders completed analysis data.
 * Port of reactjs/src/pages/analyse/FullAnalysisResults.tsx
 *
 * Handles both new payload-based and legacy analysis.* response shapes.
 * Plotly is dynamically imported (SSR-unsafe).
 */
import { useState, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Lock } from "lucide-react";
import { useAuth } from "@/platform/auth/useAuth";
import { downloadExcel, downloadPdf } from "../api";
import type { BackendAnalysisResponse, DomainInsight, AnalysisState } from "../types";
import type { PlotParams } from "react-plotly.js";

function useThemeColors() {
  const [isLight, setIsLight] = useState(false);
  useEffect(() => {
    const check = () => setIsLight(document.documentElement.classList.contains("theme-light"));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isLight;
}

const Plot = dynamic<PlotParams>(
  () => import("react-plotly.js"),
  {
    ssr: false,
    loading: () => (
      <div style={{ height: 420, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-soft)" }}>
        Loading chart...
      </div>
    ),
  }
);

interface Props {
  analysisState: AnalysisState;
  jobId?: string;
}

// ─── Formatting helpers ────────────────────────────────────────────────────────

const NAME_ACRONYMS = new Set(["abc", "xyz", "sku", "skus", "kpi", "doh", "cv", "id", "po", "soh"]);

function fmtName(name: string): string {
  return name.replace(/calc_/g, "").replace(/_/g, " ").split(" ")
    .map((w) => NAME_ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function fmtLabel(key: string): string {
  return key.replace(/_/g, " ")
    .replace(/\bpct\b/gi, "%")
    .replace(/\bsku\b/gi, "SKU")
    .replace(/\bskus\b/gi, "SKUs")
    .replace(/\s+/g, " ")
    .trim();
}

function fmtCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

function fmtVal(num: any, key = ""): string {
  if (typeof num !== "number") return String(num ?? "—");
  const k = key.toLowerCase();
  // Count fields — never add % or $ (check before spend/value which may appear in the same key)
  if (k.endsWith("count") || k.endsWith("_count") || k.includes("count_") || k === "count" ||
      k.includes("supplier_count") || k.includes("transaction_count"))
    return Math.round(num).toLocaleString();
  if (k.includes("percentage") || k.includes("percent") || k.includes("pct_") || k.startsWith("pct") || (k.includes("rate") && !k.includes("rate_")))
    return Math.abs(num) > 1 ? `${num.toFixed(2)}%` : `${(num * 100).toFixed(2)}%`;
  if (k.includes("spend") || k.includes("amount") || k.includes("revenue") || k.includes("cost") || k.includes("value") || k.includes("price") || k.includes("savings"))
    return fmtCurrency(num);
  return Number.isInteger(num) ? num.toLocaleString() : num.toFixed(2);
}

// ─── Severity styling ─────────────────────────────────────────────────────────

const SEV_STYLE: Record<string, { bg: string; border: string; color: string; label: string }> = {
  critical: { bg: "rgba(239,68,68,0.06)", border: "rgba(239,68,68,0.25)", color: "#ef4444", label: "bg-red-100 text-red-800 border-red-200" },
  warning:  { bg: "rgba(251,146,60,0.06)",  border: "rgba(251,146,60,0.25)",  color: "#f97316", label: "bg-orange-100 text-orange-800 border-orange-200" },
  success:  { bg: "rgba(34,197,94,0.06)",   border: "rgba(34,197,94,0.25)",   color: "#22c55e", label: "bg-green-100 text-green-800 border-green-200" },
  info:     { bg: "rgba(99,102,241,0.06)",  border: "rgba(99,102,241,0.25)",  color: "#6366f1", label: "bg-blue-100 text-blue-800 border-blue-200" },
};
function sevStyle(s: string) { return SEV_STYLE[s] ?? SEV_STYLE.info; }

// ─── SortableTable — sortable, searchable, paginated, downloadable ────────────

interface TableProps {
  title?: string;
  rows: Record<string, any>[];
  defaultSortCol?: string;
  defaultSortDesc?: boolean;
  pageSize?: number;
}

function SortableTable({ title, rows, defaultSortCol, defaultSortDesc = true, pageSize = 10 }: TableProps) {
  const cols = rows.length > 0 ? Object.keys(rows[0]) : [];

  const [sortCol, setSortCol]     = useState(defaultSortCol ?? cols[0] ?? "");
  const [sortDesc, setSortDesc]   = useState(defaultSortDesc);
  const [page, setPage]           = useState(0);
  const [search, setSearch]       = useState("");
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);

  // Detect which columns are numeric (check first non-null value)
  const numericCols = new Set<string>(
    cols.filter(c => {
      const sample = rows.find(r => r[c] != null)?.[c];
      return typeof sample === "number" || (!isNaN(parseFloat(String(sample))) && String(sample).trim() !== "");
    })
  );

  function isNumeric(c: string) { return numericCols.has(c); }

  // colFilters for numeric cols stored as "min|max", for text as plain string
  function getNumericFilter(c: string): { min: string; max: string } {
    const v = colFilters[c] ?? "";
    const [min, max] = v.split("|");
    return { min: min ?? "", max: max ?? "" };
  }

  function setNumericFilter(c: string, side: "min" | "max", val: string) {
    setColFilters(prev => {
      const { min, max } = { ...{ min: "", max: "" }, ...(() => { const v = prev[c] ?? ""; const [a, b] = v.split("|"); return { min: a ?? "", max: b ?? "" }; })() };
      const next = side === "min" ? `${val}|${max}` : `${min}|${val}`;
      return { ...prev, [c]: next };
    });
    setPage(0);
  }

  function setTextFilter(c: string, val: string) {
    setColFilters(prev => ({ ...prev, [c]: val }));
    setPage(0);
  }

  function clearAllFilters() {
    setColFilters({});
    setSearch("");
    setPage(0);
  }

  const activeFilterCount = Object.values(colFilters).filter(v => v && v !== "|").length + (search ? 1 : 0);

  // Apply global search + per-column filters
  const filtered = rows.filter(row => {
    // global search
    if (search && !cols.some(c => String(row[c] ?? "").toLowerCase().includes(search.toLowerCase()))) return false;
    // per-column filters
    for (const c of cols) {
      const fv = colFilters[c];
      if (!fv || fv === "|") continue;
      if (isNumeric(c)) {
        const { min, max } = getNumericFilter(c);
        const num = typeof row[c] === "number" ? row[c] : parseFloat(String(row[c] ?? "").replace(/[^0-9.-]/g, ""));
        if (!isNaN(num)) {
          if (min !== "" && num < parseFloat(min)) return false;
          if (max !== "" && num > parseFloat(max)) return false;
        }
      } else {
        if (!String(row[c] ?? "").toLowerCase().includes(fv.toLowerCase())) return false;
      }
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    const an = typeof av === "number" ? av : parseFloat(String(av).replace(/[^0-9.-]/g, ""));
    const bn = typeof bv === "number" ? bv : parseFloat(String(bv).replace(/[^0-9.-]/g, ""));
    if (!isNaN(an) && !isNaN(bn)) return sortDesc ? bn - an : an - bn;
    return sortDesc ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  // clamp page in case filters reduce total pages
  const safePage = Math.min(page, totalPages - 1);
  const slice = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  // Build stable page window: always show up to 7 buttons
  function pageWindow(total: number, current: number): number[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    let start = Math.max(0, current - 3);
    const end = Math.min(total - 1, start + 6);
    start = Math.max(0, end - 6);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  function handleSort(col: string) {
    if (col === sortCol) setSortDesc(!sortDesc);
    else { setSortCol(col); setSortDesc(true); }
    setPage(0);
  }

  function handleSearch(val: string) { setSearch(val); setPage(0); }

  function downloadCSV() {
    const header = cols.join(",");
    const body = sorted.map(r => cols.map(c => {
      const v = String(r[c] ?? "");
      return v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(",")).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${(title || "table").replace(/\s+/g, "_").toLowerCase()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  // Styles using only defined token variables
  const BORDER   = "1px solid var(--line-strong)";
  const BG_TABLE = "var(--paper-2)";
  const BG_HEAD  = "var(--paper-3)";
  const BG_ROW   = "var(--paper-2)";
  const BG_ALT   = "var(--paper)";

  const INPUT_STYLE: React.CSSProperties = {
    fontSize: 11, background: "var(--paper)", border: BORDER,
    borderRadius: 4, color: "var(--ink)", outline: "none",
    padding: "3px 6px", width: "100%", boxSizing: "border-box",
  };

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Table heading */}
      {title && (
        <p style={{ margin: "48px 0 10px 0px", fontSize: 15, fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.01em", paddingTop: 8 }}>
          {title}
        </p>
      )}
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        {/* title moved above */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
          {/* Search */}
          <div style={{ position: "relative" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-mute)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              value={search}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search…"
              style={{
                paddingLeft: 28, paddingRight: 10, paddingTop: 5, paddingBottom: 5,
                fontSize: 12, background: BG_TABLE, border: BORDER,
                borderRadius: 6, color: "var(--ink)", outline: "none",
                width: 160,
              }}
            />
          </div>
          {/* Filter toggle */}
          <button onClick={() => setShowFilters(f => !f)} title="Column filters"
            style={{
              display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", fontSize: 12,
              border: BORDER, borderRadius: 6, cursor: "pointer", fontWeight: 600,
              background: showFilters ? "var(--paper-3)" : "none",
              color: activeFilterCount > 0 ? "var(--indigo)" : "var(--ink-soft)",
            }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearAllFilters} title="Clear all filters"
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 8px", fontSize: 11, border: BORDER, borderRadius: 6, background: "none", cursor: "pointer", color: "var(--ink-mute)" }}>
              ✕ Clear
            </button>
          )}
          {/* CSV download */}
          <button onClick={downloadCSV} title="Download CSV"
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", fontSize: 12, border: "none", borderRadius: 6, background: "var(--indigo)", cursor: "pointer", color: "#fff", fontWeight: 600 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", border: BORDER, borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", background: BG_TABLE }}>
          <thead>
            {/* Sort header */}
            <tr>
              {cols.map((c) => (
                <th key={c} onClick={() => handleSort(c)}
                  style={{
                    padding: "9px 13px", textAlign: "left", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-soft)",
                    background: BG_HEAD, cursor: "pointer", whiteSpace: "nowrap",
                    borderBottom: showFilters ? "none" : BORDER, userSelect: "none",
                  }}>
                  {fmtLabel(c)}
                  {sortCol === c && (
                    <span style={{ marginLeft: 4, opacity: 0.7 }}>{sortDesc ? "↓" : "↑"}</span>
                  )}
                </th>
              ))}
            </tr>
            {/* Column filter row */}
            {showFilters && (
              <tr>
                {cols.map((c) => (
                  <th key={c} style={{ padding: "5px 8px", background: BG_HEAD, borderBottom: BORDER, verticalAlign: "top" }}>
                    {isNumeric(c) ? (
                      <div style={{ display: "flex", gap: 3 }}>
                        <input
                          type="number"
                          placeholder="Min"
                          value={getNumericFilter(c).min}
                          onChange={e => setNumericFilter(c, "min", e.target.value)}
                          style={{ ...INPUT_STYLE, width: "50%" }}
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={getNumericFilter(c).max}
                          onChange={e => setNumericFilter(c, "max", e.target.value)}
                          style={{ ...INPUT_STYLE, width: "50%" }}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        placeholder={`Filter…`}
                        value={colFilters[c] ?? ""}
                        onChange={e => setTextFilter(c, e.target.value)}
                        style={INPUT_STYLE}
                      />
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {slice.length === 0 ? (
              <tr>
                <td colSpan={cols.length} style={{ padding: "20px", textAlign: "center", fontSize: 13, color: "var(--ink-mute)" }}>
                  No results{search ? ` for "${search}"` : activeFilterCount > 0 ? " — try adjusting filters" : ""}
                </td>
              </tr>
            ) : slice.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? BG_ROW : BG_ALT }}>
                {cols.map((c, ci) => (
                  <td key={c} style={{
                    padding: "8px 13px", fontSize: 13,
                    borderBottom: i < slice.length - 1 ? BORDER : "none",
                    fontWeight: ci === 0 ? 600 : 400,
                    color: ci === 0 ? "var(--ink)" : "var(--ink-soft)",
                  }}>
                    {typeof row[c] === "number" ? fmtVal(row[c], c) : String(row[c] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination + row count */}
      {(totalPages > 1 || filtered.length !== rows.length) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "var(--ink-mute)" }}>
            {filtered.length} row{filtered.length !== 1 ? "s" : ""}
            {filtered.length !== rows.length ? ` (filtered from ${rows.length})` : ""}
          </span>
          {totalPages > 1 && (
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}
                style={{ padding: "3px 8px", fontSize: 11, borderRadius: 4, border: BORDER, background: "none", cursor: safePage === 0 ? "default" : "pointer", opacity: safePage === 0 ? 0.3 : 1, color: "var(--ink-soft)" }}>‹</button>
              {pageWindow(totalPages, safePage).map(pg => (
                <button key={pg} onClick={() => setPage(pg)} style={{
                  padding: "3px 8px", fontSize: 11, borderRadius: 4, border: BORDER, cursor: "pointer",
                  background: pg === safePage ? "var(--indigo)" : "none",
                  color: pg === safePage ? "#fff" : "var(--ink-soft)",
                }}>{pg + 1}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage === totalPages - 1}
                style={{ padding: "3px 8px", fontSize: 11, borderRadius: 4, border: BORDER, background: "none", cursor: safePage === totalPages - 1 ? "default" : "pointer", opacity: safePage === totalPages - 1 ? 0.3 : 1, color: "var(--ink-soft)" }}>›</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Insight card (full detail) ───────────────────────────────────────────────

function InsightCard({ insight, idx }: { insight: DomainInsight; idx: number }) {
  const s = sevStyle(insight.severity);
  return (
    <div style={{ padding: "16px 20px", borderRadius: 10, background: s.bg, border: `1px solid ${s.border}`, display: "flex", gap: 14 }}>
      <div style={{ flexShrink: 0, marginTop: 2 }}><SevIcon sev={insight.severity} /></div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={{ padding: "1px 8px", borderRadius: 20, fontSize: 11, border: `1px solid ${s.color}`, color: s.color, textTransform: "uppercase" }}>{insight.severity}</span>
          {insight.priority && <span style={{ padding: "1px 8px", borderRadius: 20, fontSize: 11, border: "1px solid var(--line-soft)", textTransform: "uppercase" }}>{insight.priority} priority</span>}
          {typeof insight.confidence === "number" && <span style={{ fontSize: 11, color: "var(--ink-soft)" }}>{Math.round(insight.confidence * 100)}% confidence</span>}
        </div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{insight.insight}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {insight.metric && <p style={{ margin: 0, fontSize: 12 }}><strong style={{ color: "var(--indigo)" }}>Key Metric:</strong> <span style={{ color: "var(--ink-soft)" }}>{insight.metric}</span></p>}
          {insight.business_impact && <p style={{ margin: 0, fontSize: 12 }}><strong style={{ color: "var(--indigo)" }}>Business Impact:</strong> <span style={{ color: "var(--ink-soft)" }}>{insight.business_impact}</span></p>}
          {insight.root_cause && <p style={{ margin: 0, fontSize: 12 }}><strong style={{ color: "var(--indigo)" }}>Root Cause:</strong> <span style={{ color: "var(--ink-soft)" }}>{insight.root_cause}</span></p>}
        </div>
        {insight.recommendation && (
          <div style={{ paddingTop: 8, borderTop: "1px solid var(--line-soft)" }}>
            <p style={{ margin: 0, fontSize: 12 }}><strong style={{ color: "#22c55e" }}>💡 Recommendation:</strong> <span style={{ color: "var(--ink-soft)" }}>{insight.recommendation}</span></p>
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 11, color: "var(--ink-soft)" }}>
          {insight.timeline && <span>⏱ {insight.timeline}</span>}
          {insight.effort && <span>💪 {insight.effort} effort</span>}
          {(insight.kpi_source || insight.tool_source) && <span>📊 {insight.kpi_source || insight.tool_source}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── ToolSection — renders one tool's metrics + insights + nested tables ──────

const TOOL_META = new Set(["success","tool","tool_name","headline","description",
  "execution_time_seconds","analysis_type","metric_type","metrics","insights"]);

function ToolSection({ toolKey, toolData }: { toolKey: string; toolData: any }) {
  const [showDescription, setShowDescription] = useState(false);
  const toolMetrics = toolData.metrics || {};
  const rawInsights: any[] = toolData.insights || [];
  const displayLabel = toolData.headline || (toolData.tool_name || fmtName(toolKey)).replace(/_/g, " ");
  const description = toolData.description;
  const scalarMetrics = Object.entries(toolMetrics).filter(([, v]) => typeof v !== "object" && !Array.isArray(v));
  const objMetrics    = Object.entries(toolMetrics).filter(([, v]) => v && typeof v === "object" && !Array.isArray(v));
  const arrMetrics    = Object.entries(toolMetrics).filter(([, v]) => Array.isArray(v) && (v as any[]).length > 0);
  const directFields  = Object.entries(toolData).filter(([k, v]) => !TOOL_META.has(k) && v && typeof v === "object");

  return (
    <div style={{ border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--paper-2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{displayLabel}</p>
          {description && (
            <button
              onClick={() => setShowDescription(!showDescription)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: "1px solid var(--line-soft)",
                background: "none",
                cursor: "pointer",
                padding: 0,
              }}
              title="View description"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--indigo)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </button>
          )}
        </div>
        {toolData.execution_time_seconds && <span style={{ fontSize: 11, color: "var(--ink-soft)" }}>{toolData.execution_time_seconds.toFixed(2)}s</span>}
      </div>
      {showDescription && description && (
        <div style={{ padding: "12px 16px", background: "var(--paper-1)", borderTop: "1px solid var(--line-soft)", borderBottom: "1px solid var(--line-soft)" }}>
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: "var(--ink-soft)" }}>{description}</p>
        </div>
      )}
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Scalar KPIs */}
        {scalarMetrics.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8 }}>
            {scalarMetrics.slice(0, 6).map(([mk, mv]: [string, any]) => (
              <div key={mk} style={{ padding: "8px 10px", background: "var(--paper-1)", borderRadius: 8 }}>
                <p style={{ margin: "0 0 2px", fontSize: 11, color: "var(--ink-soft)", textTransform: "capitalize" }}>{fmtLabel(mk)}</p>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>{typeof mv === "number" ? fmtVal(mv, mk) : String(mv)}</p>
              </div>
            ))}
          </div>
        )}
        {/* Nested object metrics (e.g. abc_summary) */}
        {objMetrics.map(([mk, mv]: [string, any]) => {
          const entries = Object.entries(mv);
          if (entries.length === 0) return null;
          const firstVal = entries[0][1];
          const isNestedObj = firstVal && typeof firstVal === "object" && !Array.isArray(firstVal);
          if (isNestedObj) {
            const colKeys = Object.keys(firstVal as object);
            const tableRows = entries.map(([rowKey, rowVal]: [string, any]) => ({
              category: rowKey, ...Object.fromEntries(colKeys.map(ck => [ck, rowVal[ck]])),
            }));
            const sortKey = colKeys.find(k => /spend|amount|count|value|cost|pct|percent/i.test(k)) ?? colKeys[0];
            return <SortableTable key={mk} title={fmtName(mk)} rows={tableRows} defaultSortCol={sortKey} />;
          }
          // Simple key-value object
          const isPercField = mk.includes("pareto_suppliers") || mk.includes("top_5") || mk.toLowerCase().includes("percent");
          const kvRows = entries.map(([k, v]) => ({ label: k, value: typeof v === "number" && isPercField ? `${(v as number).toFixed(2)}%` : v }));
          const numericFirst = typeof (kvRows[0]?.value) === "number";
          return <SortableTable key={mk} title={fmtName(mk)} rows={kvRows} defaultSortCol={numericFirst ? "value" : "label"} />;
        })}
        {/* Array metrics (top items tables) */}
        {arrMetrics.map(([mk, mv]: [string, any]) => {
          const arr = mv as any[];
          if (typeof arr[0] !== "object") return null;
          const badCols = Object.keys(arr[0]).filter(ck => Array.isArray(arr[0][ck]) && typeof arr[0][ck][0] === "object");
          const colKeys = Object.keys(arr[0]).filter(ck => !badCols.includes(ck));
          const rows = arr.map(r => Object.fromEntries(colKeys.map(ck => [ck, r[ck]])));
          const sortKey = colKeys.find(k => /spend|amount|count|value|cost|pct|percent/i.test(k)) ?? colKeys[1] ?? colKeys[0];
          return <SortableTable key={mk} title={fmtName(mk)} rows={rows} defaultSortCol={sortKey} />;
        })}
        {/* Direct data fields (logistics-style sections) */}
        {directFields.map(([dk, dv]: [string, any]) => {
          if (Array.isArray(dv)) {
            if (!dv.length || typeof dv[0] !== "object") return null;
            const colKeys = Object.keys(dv[0]);
            const rows = dv.map(r => Object.fromEntries(colKeys.map(ck => [ck, r[ck]])));
            const sortKey = colKeys.find(k => /spend|amount|count|value|cost|pct|percent/i.test(k)) ?? colKeys[1] ?? colKeys[0];
            return <SortableTable key={dk} title={fmtName(dk)} rows={rows} defaultSortCol={sortKey} />;
          }
          const entries = Object.entries(dv);
          if (!entries.length) return null;
          const firstVal = entries[0][1];
          const isNestedObj = firstVal && typeof firstVal === "object" && !Array.isArray(firstVal);
          if (isNestedObj) {
            const colKeys = Object.keys(firstVal as object);
            const rows = entries.map(([rk, rv]: [string, any]) => ({ category: fmtLabel(rk), ...Object.fromEntries(colKeys.map(ck => [ck, rv[ck]])) }));
            return <SortableTable key={dk} title={fmtName(dk)} rows={rows} />;
          }
          const scalars = entries.filter(([, v]) => typeof v !== "object" && !Array.isArray(v));
          if (!scalars.length) return null;
          return (
            <div key={dk}>
              <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-soft)" }}>{fmtName(dk)}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 8 }}>
                {scalars.map(([k, v]: [string, any]) => (
                  <div key={k} style={{ padding: "8px 10px", background: "var(--paper-1)", borderRadius: 8 }}>
                    <p style={{ margin: "0 0 2px", fontSize: 11, color: "var(--ink-soft)", textTransform: "capitalize" }}>{fmtLabel(k)}</p>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 500 }}>{typeof v === "number" ? fmtVal(v, k) : String(v)}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {/* Insights from this tool */}
        {rawInsights.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {rawInsights.map((ins: any, i: number) => {
              const s = sevStyle(ins.severity || "info");
              // Normalise insight text — handles both {insight, message} shapes
              const insightText = ins.insight || ins.message || "";
              // extra field (e.g. "Key carriers: FedEx, UPS…")
              const extra = ins.carriers?.length ? `Key carriers: ${ins.carriers.slice(0, 5).join(", ")}` : ins.extra;
              return (
                <div key={i} style={{ padding: "10px 14px", borderRadius: 8, background: s.bg, border: `1px solid ${s.border}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ flexShrink: 0, marginTop: 1 }}><SevIcon sev={ins.severity || "info"} size={14} /></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{insightText}</p>
                    {extra && <p style={{ margin: 0, fontSize: 11, color: "var(--ink-soft)", fontStyle: "italic" }}>{extra}</p>}
                    {ins.metric && <p style={{ margin: 0, fontSize: 11, color: "var(--ink-soft)" }}>📊 {ins.metric}</p>}
                    {ins.business_impact && <p style={{ margin: 0, fontSize: 11, color: "var(--ink-soft)" }}><strong>Impact:</strong> {ins.business_impact}</p>}
                    {ins.root_cause && <p style={{ margin: 0, fontSize: 11, color: "var(--ink-soft)" }}><strong>Root cause:</strong> {ins.root_cause}</p>}
                    {ins.recommendation && (
                      <p style={{ margin: "2px 0 0", fontSize: 11, borderTop: "1px solid var(--line-soft)", paddingTop: 4 }}>
                        💡 <span style={{ color: "var(--ink-soft)" }}>{ins.recommendation}</span>
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MetricSection — renders one metric group from metrics object ─────────────

function MetricSection({ name, data, analysisId }: { name: string; data: any; analysisId?: string }) {
  const [open, setOpen] = useState(true);

  if (typeof data !== "object" || data === null) {
    if (name === "timestamp") return null;
    return (
      <div style={{ padding: "12px 16px", background: "var(--paper-1)", border: "1px solid var(--line-soft)", borderRadius: 10 }}>
        <p style={{ margin: 0, fontSize: 11, color: "var(--ink-soft)" }}>{fmtName(name)}</p>
        <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 600 }}>{String(data)}</p>
      </div>
    );
  }

  if (data.error && data.error_type) {
    return (
      <div style={{ padding: "14px 16px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10 }}>
        <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: "#ef4444" }}>{fmtName(name)} — Error</p>
        <p style={{ margin: 0, fontSize: 12, color: "#ef4444" }}>{data.error_type}: {data.error}</p>
        {data.traceback && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 11, cursor: "pointer", color: "#ef4444" }}>View Traceback</summary>
            <pre style={{ marginTop: 6, fontSize: 10, overflowX: "auto", background: "rgba(239,68,68,0.06)", padding: 8, borderRadius: 6 }}>{data.traceback}</pre>
          </details>
        )}
      </div>
    );
  }

  // Nested basic_metrics collection (each value has title/value/formatted)
  const isNestedColl = Object.values(data).length > 0 &&
    Object.values(data).every((v: any) => v && typeof v === "object" && ("title" in v || "value" in v || "formatted" in v));

  if (isNestedColl) {
    return (
      <div style={{ background: "var(--paper-1)", border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
        <button onClick={() => setOpen(!open)} style={{ width: "100%", padding: "14px 20px", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(99,102,241,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}><BarIcon size={18} /></div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{fmtName(name)}</p>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-soft)" }}>{Object.keys(data).length} metrics</p>
            </div>
          </div>
          <ChevronIcon open={open} />
        </button>
        {open && (
          <div style={{ padding: "0 20px 20px" }}>
            {/* Scalar metrics — grid of stat cards */}
            {(() => {
              const scalarEntries: [string, any][] = [];
              const tableEntries: [string, any][] = [];
              Object.entries(data).forEach(([key, metric]: [string, any]) => {
                const isObjVal = typeof metric.value === "object" && metric.value !== null;
                const isObjFmt = typeof metric.formatted === "object" && metric.formatted !== null;
                if (isObjFmt || isObjVal) tableEntries.push([key, metric]);
                else scalarEntries.push([key, metric]);
              });
              return (
                <>
                  {scalarEntries.length > 0 && (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: 12,
                      marginBottom: tableEntries.length > 0 ? 20 : 0,
                    }}>
                      {scalarEntries.map(([key, metric]) => (
                        <div key={key} style={{
                          padding: "16px 18px",
                          background: "var(--paper-2)",
                          border: "1px solid var(--line-soft)",
                          borderRadius: 10,
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}>
                          <p style={{ margin: 0, fontSize: 12, color: "var(--ink-soft)", fontWeight: 500 }}>
                            {metric.title || fmtName(key)}
                          </p>
                          {metric.explanation && (
                            <p style={{ margin: 0, fontSize: 11, color: "var(--ink-soft)", opacity: 0.6, lineHeight: 1.4 }}>
                              {metric.explanation}
                            </p>
                          )}
                          <p style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 700, letterSpacing: "-0.5px" }}>
                            {metric.formatted ?? fmtVal(metric.value, key)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {tableEntries.map(([key, metric]) => {
                    const isObjFmt = typeof metric.formatted === "object" && metric.formatted !== null;
                    const src = isObjFmt ? metric.formatted : metric.value;
                    const rows = Object.entries(src).map(([k, v]) => ({ label: k, value: v as any }));
                    return (
                      <div key={key} style={{ marginBottom: 12 }}>
                        {metric.explanation && <p style={{ fontSize: 11, color: "var(--ink-soft)", marginBottom: 4 }}>{metric.explanation}</p>}
                        <SortableTable title={metric.title || fmtName(key)} rows={rows} defaultSortCol="value" />
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  }

  // Tool results collection (advanced_metrics — has tool_name/success)
  const isToolColl = Object.values(data).length > 0 &&
    Object.values(data).some((v: any) => v && typeof v === "object" && ("tool_name" in v || "success" in v));

  if (isToolColl) {
    const toolEntries = Object.entries(data).filter(([, v]: [string, any]) => v && typeof v === "object");
    const totalIns = toolEntries.reduce((acc, [, t]: [string, any]) => acc + (t.insights?.length || 0), 0);
    return (
      <div style={{ background: "var(--paper-1)", border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
        <button onClick={() => setOpen(!open)} style={{ width: "100%", padding: "14px 20px", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(139,92,246,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}><TrendIcon size={18} /></div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{fmtName(name)}</p>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-soft)" }}>{toolEntries.length} analyses · {totalIns} insights</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {analysisId && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadExcel(analysisId);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--line-soft)",
                  background: "var(--paper-2)",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                }}
                title="Download Excel report with detailed metrics"
              >
                <SheetIcon /> Download Excel
              </button>
            )}
            <ChevronIcon open={open} />
          </div>
        </button>
        {open && (
          <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            {toolEntries.map(([tk, td]: [string, any]) => <ToolSection key={tk} toolKey={tk} toolData={td} />)}
          </div>
        )}
      </div>
    );
  }

  // Detailed structure (has .summary and/or array fields — new structure)
  if (data.summary || data.vendor_performance) {
    return (
      <div style={{ background: "var(--paper-1)", border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
        <button onClick={() => setOpen(!open)} style={{ width: "100%", padding: "14px 20px", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(99,102,241,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}><BarIcon size={18} /></div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{fmtName(name)}</p>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-soft)" }}>{data.summary ? `${Object.keys(data.summary).length} key indicators` : "Detailed analysis"}</p>
            </div>
          </div>
          <ChevronIcon open={open} />
        </button>
        {/* Summary stats — always visible when expanded */}
        {data.summary && (
          <div style={{ padding: "0 20px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10 }}>
            {Object.entries(data.summary).slice(0, 8).map(([k, v]: [string, any]) => (
              <div key={k} style={{ padding: "10px 12px", background: "var(--paper-2)", borderRadius: 8 }}>
                <p style={{ margin: "0 0 4px", fontSize: 11, color: "var(--ink-soft)", textTransform: "capitalize" }}>{fmtLabel(k)}</p>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{typeof v === "number" ? fmtVal(v, k) : String(v ?? "—")}</p>
              </div>
            ))}
          </div>
        )}
        {open && (
          <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            {Object.entries(data).map(([field, val]: [string, any]) => {
              if (field === "summary" || !Array.isArray(val) || !val.length) return null;
              if (typeof val[0] !== "object") return null;
              const colKeys = Object.keys(val[0]);
              const rows = val.map((r: any) => Object.fromEntries(colKeys.map(ck => [ck, r[ck]])));
              const sortKey = colKeys.find(k => /spend|amount|count|value|cost|pct|percent/i.test(k)) ?? colKeys[1] ?? colKeys[0];
              return <SortableTable key={field} title={fmtName(field)} rows={rows} defaultSortCol={sortKey} />;
            })}
          </div>
        )}
      </div>
    );
  }

  // Legacy simple metric
  const displayValue = data?.formatted || data?.title ||
    (typeof data?.value === "object" ? `${Object.keys(data.value).length} items` : String(data?.value ?? data));
  if (typeof displayValue === "string" && (displayValue.startsWith("[{") || displayValue === "[object Object]")) return null;
  return (
    <div style={{ padding: "12px 16px", background: "var(--paper-1)", border: "1px solid var(--line-soft)", borderRadius: 10 }}>
      <p style={{ margin: 0, fontSize: 11, color: "var(--ink-soft)" }}>{data?.title || fmtName(name)}</p>
      <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 600 }}>{displayValue}</p>
    </div>
  );
}

// ─── Extraction helpers ───────────────────────────────────────────────────────

function extractMetrics(r: BackendAnalysisResponse): any | null {
  if (r.payload) {
    const dk = Object.keys(r.payload).filter(k => k !== "basic_metrics");
    if (dk.length > 0 && r.payload[dk[0]]?.metrics) return r.payload[dk[0]].metrics;
  }
  const domainKey = Object.keys(r).find(k => k.endsWith("_langgraph") || k === "procurement" || k === "inventory");
  if (domainKey && (r as any)[domainKey]?.metrics) return (r as any)[domainKey].metrics;
  if (r.metrics) {
    const m = r.metrics;
    if (m.basic_metrics || m.advanced_metrics) return m;
    const first = Object.values(m)[0];
    if (first && typeof first === "object" && !(first as any).summary) return first as any;
    return m;
  }
  if (r.analysis?.metrics) return r.analysis.metrics;
  return null;
}

function extractInsights(r: BackendAnalysisResponse): DomainInsight[] {
  // payload domain
  if (r.payload) {
    const dk = Object.keys(r.payload).filter(k => !["basic_metrics","sensei_insights","cross_domain_insights"].includes(k));
    if (dk.length > 0) {
      const first = r.payload[dk[0]];
      if (first?.domain_insights?.length) return first.domain_insights;
      if (first?.insights?.length) return first.insights;
    }
  }
  const domainKey = Object.keys(r).find(k => k.endsWith("_langgraph") || k === "procurement" || k === "inventory");
  if (domainKey) {
    const d = (r as any)[domainKey];
    if (d?.domain_insights?.length) return d.domain_insights;
  }
  const ins = r.insights as any;
  if (Array.isArray(ins)) return ins;
  if (ins?.domain_insights?.length) return ins.domain_insights;
  if (ins?.insights?.length) return ins.insights;
  return [];
}

function extractCharts(r: BackendAnalysisResponse): any[] {
  if (r.payload) {
    const dk = Object.keys(r.payload).filter(k => k !== "basic_metrics");
    if (dk.length > 0 && Array.isArray(r.payload[dk[0]]?.charts)) return r.payload[dk[0]].charts;
  }
  const domainKey = Object.keys(r).find(k => k.endsWith("_langgraph") || k === "procurement" || k === "inventory");
  if (domainKey && Array.isArray((r as any)[domainKey]?.charts)) return (r as any)[domainKey].charts;
  if (Array.isArray(r.charts)) return r.charts as any[];
  if (r.charts && typeof r.charts === "object") return Object.values(r.charts);
  if (Array.isArray((r as any).chart_data)) return (r as any).chart_data;
  return [];
}

function extractExecutiveSummary(r: BackendAnalysisResponse): string | null {
  const raw = r.sensei_insights?.summary || r.payload?.sensei_insights?.summary
    || r.payload?.executive_summary || r.executive_summary || r.summary;
  if (typeof raw === "string" && !raw.includes("Parsing failed") && !raw.includes("invalid JSON")) return raw;
  return null;
}

const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
function sortCharts(charts: any[]): any[] {
  return [...charts].sort((a, b) => (PRIORITY_ORDER[a.priority?.toLowerCase()] ?? 3) - (PRIORITY_ORDER[b.priority?.toLowerCase()] ?? 3));
}

// Palette used across all chart traces (vivid, high-contrast on dark bg)
const DARK_PALETTE = [
  "#818cf8", // indigo-400
  "#34d399", // emerald-400
  "#f472b6", // pink-400
  "#fb923c", // orange-400
  "#38bdf8", // sky-400
  "#a78bfa", // violet-400
  "#facc15", // yellow-400
  "#4ade80", // green-400
  "#f87171", // red-400
  "#22d3ee", // cyan-400
  "#c084fc", // purple-400
  "#fbbf24", // amber-400
];

const LIGHT_PALETTE = [
  "#6366f1", // indigo-500
  "#10b981", // emerald-500
  "#ec4899", // pink-500
  "#f97316", // orange-500
  "#0ea5e9", // sky-500
  "#8b5cf6", // violet-500
  "#eab308", // yellow-500
  "#22c55e", // green-500
  "#ef4444", // red-500
  "#06b6d4", // cyan-500
  "#a855f7", // purple-500
  "#f59e0b", // amber-500
];

const DARK_BG   = "#0F0F1A"; // --paper-1
const DARK_PLOT = "#11111C"; // --paper-2
const DARK_GRID = "rgba(255,255,255,0.07)";
const DARK_LINE = "rgba(255,255,255,0.15)";
const DARK_TEXT = "rgba(255,255,255,0.72)";
const DARK_FONT = { family: "var(--font-inter-tight), Inter, system-ui, sans-serif", size: 12, color: DARK_TEXT };

/**
 * Remap light colors in a trace to vivid dark-theme equivalents.
 * Strategy: never remove backend colors wholesale — only replace colors that are
 * provably invisible on a dark background (white, near-white, light grays, or
 * Plotly's default light-theme palette entries).
 */

// Plotly's default light-theme colorway (these look fine on white, bad on dark)
const PLOTLY_LIGHT_DEFAULTS = new Set([
  "#636efa","#ef553b","#00cc96","#ab63fa","#ffa15a","#19d3f3","#ff6692",
  "#b6e880","#ff97ff","#fecb52",
  // pure/near-white that backend may emit
  "#ffffff","#fff","white","#f0f0f0","#fafafa","#eeeeee","#e0e0e0",
  "rgba(255,255,255,1)","rgb(255,255,255)",
]);

function isLightColor(c: any): boolean {
  if (typeof c !== "string") return false;
  const lc = c.toLowerCase().trim();
  if (PLOTLY_LIGHT_DEFAULTS.has(lc)) return true;
  // hex shorthand or full: check luminance heuristic
  const hex = lc.startsWith("#") ? lc : null;
  if (hex) {
    const full = hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
    if (full.length === 7) {
      const r = parseInt(full.slice(1,3),16);
      const g = parseInt(full.slice(3,5),16);
      const b = parseInt(full.slice(5,7),16);
      // relative luminance > 0.55 → too light for dark bg
      return (0.299*r + 0.587*g + 0.114*b) / 255 > 0.55;
    }
  }
  return false;
}

function remapColor(c: any, fallback: string): any {
  if (Array.isArray(c)) return c.map((v, i) => isLightColor(v) ? DARK_PALETTE[i % DARK_PALETTE.length] : v);
  return isLightColor(c) ? fallback : c;
}

function darkTrace(trace: any, idx: number): any {
  const fallback = DARK_PALETTE[idx % DARK_PALETTE.length];
  const t = { ...trace };

  // Marker color (bars, scatter points, etc.)
  if (t.marker) {
    t.marker = { ...t.marker };
    if (t.marker.color !== undefined) {
      t.marker.color = remapColor(t.marker.color, fallback);
    } else {
      // No color set at all — let colorway handle it (don't force one)
    }
    // Marker line — make it subtle on dark
    if (t.marker.line?.color && isLightColor(t.marker.line.color)) {
      t.marker = { ...t.marker, line: { ...t.marker.line, color: "rgba(255,255,255,0.1)" } };
    }
  }

  // Line color (scatter lines)
  if (t.line?.color && isLightColor(t.line.color)) {
    t.line = { ...t.line, color: fallback };
  }

  // Fill color — translucent version of the line color
  if (t.fillcolor && isLightColor(t.fillcolor)) {
    t.fillcolor = fallback + "28";
  }

  // Pie / donut slice colors
  if ((t.type === "pie" || t.type === "sunburst") && t.marker?.colors) {
    t.marker = { ...t.marker, colors: t.marker.colors.map((c: any, i: number) => isLightColor(c) ? DARK_PALETTE[i % DARK_PALETTE.length] : c) };
  }
  if (t.type === "pie" || t.type === "sunburst") {
    t.textfont = { ...(t.textfont ?? {}), color: "#fff" };
    t.outsidetextfont = { ...(t.outsidetextfont ?? {}), color: DARK_TEXT };
  }

  // Heatmap colorscale: replace if missing
  if ((t.type === "heatmap" || t.type === "heatmapgl") && !t.colorscale) {
    t.colorscale = [[0,"#0d1b2a"],[0.25,"#1e3a5f"],[0.5,"#5B6CFF"],[0.75,"#a855f7"],[1,"#EC4899"]];
  }

  // Choropleth / scattergeo — dark map colors
  if (t.type === "choropleth" || t.type === "choroplethmap") {
    // Always replace — backend choropleth colorscales are always light-theme blues
    t.colorscale = [[0,"#0d1117"],[0.2,"#0d1b2a"],[0.4,"#1e3a8a"],[0.7,"#5B6CFF"],[1,"#818cf8"]];
    t.marker = { ...(t.marker ?? {}), line: { color: "rgba(255,255,255,0.2)", width: 0.5 } };
    t.colorbar = {
      ...(t.colorbar ?? {}),
      bgcolor: "rgba(0,0,0,0)",
      bordercolor: "rgba(255,255,255,0.15)",
      tickfont: DARK_FONT,
      ...(t.colorbar?.title ? { title: { text: typeof t.colorbar.title === "string" ? t.colorbar.title : t.colorbar.title?.text, font: DARK_FONT } } : {}),
    };
  }

  // Scattergeo
  if (t.type === "scattergeo") {
    if (t.marker && (!t.marker.color || isLightColor(t.marker.color))) {
      t.marker = { ...t.marker, color: fallback, line: { color: "rgba(255,255,255,0.3)", width: 1 } };
    }
  }

  // Waterfall: ensure visible colors
  if (t.type === "waterfall") {
    if (!t.increasing?.marker?.color || isLightColor(t.increasing.marker.color))
      t.increasing = { ...(t.increasing ?? {}), marker: { color: "#34d399" } };
    if (!t.decreasing?.marker?.color || isLightColor(t.decreasing.marker.color))
      t.decreasing = { ...(t.decreasing ?? {}), marker: { color: "#f87171" } };
    if (!t.totals?.marker?.color || isLightColor(t.totals.marker.color))
      t.totals = { ...(t.totals ?? {}), marker: { color: "#818cf8" } };
    t.connector = { ...(t.connector ?? {}), line: { color: "rgba(255,255,255,0.2)" } };
  }

  // Font color on any trace-level textfont
  if (t.textfont?.color && isLightColor(t.textfont.color)) {
    t.textfont = { ...t.textfont, color: DARK_TEXT };
  }

  return t;
}

function cleanLayout(layout: any, isDark: boolean): any {
  const builder = isDark ? buildDarkLayout : buildLightLayout;
  if (!layout) return builder({});
  const {
    template,
    paper_bgcolor, plot_bgcolor,
    font,
    colorway,
    legend,
    ...rest
  } = layout;
  const isGeo = !!rest.geo;
  return builder({ ...rest, _colorway: colorway, _isGeo: isGeo });
}

function buildDarkLayout(base: any): any {
  // Pull only semantic (non-visual) fields from each axis
  function safeAxis(ax: any) {
    if (!ax || typeof ax !== "object") return {};
    const safe: Record<string, any> = {};
    const keep = ["type","range","tickformat","dtick","tickangle","tickmode","tickvals",
                  "ticktext","nticks","categoryorder","categoryarray","autorange",
                  "fixedrange","showticklabels","separatethousands","exponentformat"];
    for (const k of keep) if (ax[k] !== undefined) safe[k] = ax[k];
    // Axis title: keep text, override font, ensure standoff so title doesn't overlap tick labels
    if (ax.title) safe.title = {
      text: typeof ax.title === "string" ? ax.title : (ax.title.text ?? ""),
      font: DARK_FONT,
      standoff: ax.title?.standoff ?? 14,
    };
    return safe;
  }

  const AXIS = {
    visible: true,
    showgrid: true,    gridcolor: DARK_GRID,  gridwidth: 1,
    showline: true,    linecolor: DARK_LINE,   linewidth: 1,
    zeroline: true,    zerolinecolor: "rgba(255,255,255,0.12)", zerolinewidth: 1,
    tickfont: DARK_FONT, titlefont: DARK_FONT, tickcolor: DARK_LINE,
  };

  const titleText = typeof base.title === "string"
    ? base.title : (base.title?.text ?? undefined);

  const baseColorway: string[] = base._colorway ?? [];
  const isGeoChart = !!base.geo || base._isGeo;

  return {
    autosize: true,
    paper_bgcolor: DARK_BG,
    plot_bgcolor:  isGeoChart ? DARK_BG : DARK_PLOT,
    font: DARK_FONT,
    // Geo charts need tight margins — the projection handles its own aspect ratio
    margin: isGeoChart
      ? { l: 0, r: 0, t: titleText ? 36 : 0, b: 0, pad: 0 }
      : { l: 80, r: 60, t: titleText ? 54 : 32, b: 120, pad: 4 },
    ...(titleText ? { title: { text: titleText, font: { ...DARK_FONT, size: 14, color: "#fff" } } } : {}),
    // Pass through only safe non-visual layout keys
    ...(base.barmode   ? { barmode:   base.barmode   } : {}),
    ...(base.barnorm   ? { barnorm:   base.barnorm   } : {}),
    ...(base.boxmode   ? { boxmode:   base.boxmode   } : {}),
    ...(base.violinmode? { violinmode:base.violinmode } : {}),
    hovermode: base.hovermode ?? "closest",
    // Axes: safe semantic fields merged, then our dark visuals win
    xaxis:  { ...safeAxis(base.xaxis),  ...AXIS, tickangle: base.xaxis?.tickangle ?? -35 },
    yaxis:  { ...safeAxis(base.yaxis),  ...AXIS },
    ...(base.xaxis2 ? { xaxis2: { ...safeAxis(base.xaxis2), ...AXIS, tickangle: base.xaxis2?.tickangle ?? -35 } } : {}),
    ...(base.yaxis2 ? { yaxis2: { ...safeAxis(base.yaxis2), ...AXIS } } : {}),
    // Geo map (choropleth / scattergeo) — dark land/ocean/border colors
    geo: {
      ...(base.geo?.projection ? { projection: base.geo.projection } : {}),
      ...(base.geo?.scope      ? { scope:      base.geo.scope      } : {}),
      ...(base.geo?.center     ? { center:     base.geo.center     } : {}),
      ...(base.geo?.lataxis    ? { lataxis:    base.geo.lataxis    } : {}),
      ...(base.geo?.lonaxis    ? { lonaxis:    base.geo.lonaxis    } : {}),
      bgcolor:        DARK_BG,
      landcolor:      "#1e2235",
      oceancolor:     "#0d1117",
      lakecolor:      "#0d1117",
      rivercolor:     "#0d1117",
      subunitcolor:   "rgba(255,255,255,0.12)",
      countrycolor:   "rgba(255,255,255,0.22)",
      coastlinecolor: "rgba(255,255,255,0.22)",
      showland:       true,
      showocean:      true,
      showlakes:      true,
      showrivers:     false,
      showcoastlines: true,
      showcountries:  true,
      showsubunits:   true,
      framecolor:     "rgba(255,255,255,0.12)",
      framewidth:     1,
    },
    legend: {
      orientation: "h" as const, y: -0.22, x: 0,
      bgcolor: "rgba(0,0,0,0)", bordercolor: "rgba(0,0,0,0)",
      font: DARK_FONT,
    },
    colorway: baseColorway.length
      ? baseColorway.map((c: string, i: number) => isLightColor(c) ? DARK_PALETTE[i % DARK_PALETTE.length] : c)
      : DARK_PALETTE,
    hoverlabel: {
      bgcolor: "#1A1A28", bordercolor: "rgba(255,255,255,0.25)",
      font: { ...DARK_FONT, color: "#fff" },
    },
    modebar: {
      bgcolor: "rgba(0,0,0,0)", color: "rgba(255,255,255,0.35)", activecolor: "#818cf8",
    },
  };
}

const LIGHT_FONT = { family: "var(--font-inter-tight), Inter, system-ui, sans-serif", size: 12, color: "#374151" };

function buildLightLayout(base: any): any {
  function safeAxis(ax: any) {
    if (!ax || typeof ax !== "object") return {};
    const safe: Record<string, any> = {};
    const keep = ["type","range","tickformat","dtick","tickangle","tickmode","tickvals",
                  "ticktext","nticks","categoryorder","categoryarray","autorange",
                  "fixedrange","showticklabels","separatethousands","exponentformat"];
    for (const k of keep) if (ax[k] !== undefined) safe[k] = ax[k];
    if (ax.title) safe.title = {
      text: typeof ax.title === "string" ? ax.title : (ax.title.text ?? ""),
      font: LIGHT_FONT,
      standoff: ax.title?.standoff ?? 14,
    };
    return safe;
  }

  const AXIS = {
    visible: true,
    showgrid: true,    gridcolor: "rgba(0,0,0,0.06)",  gridwidth: 1,
    showline: true,    linecolor: "rgba(0,0,0,0.15)",   linewidth: 1,
    zeroline: true,    zerolinecolor: "rgba(0,0,0,0.12)", zerolinewidth: 1,
    tickfont: LIGHT_FONT, titlefont: LIGHT_FONT, tickcolor: "rgba(0,0,0,0.15)",
  };

  const titleText = typeof base.title === "string"
    ? base.title : (base.title?.text ?? undefined);
  const baseColorway: string[] = base._colorway ?? [];
  const isGeoChart = !!base.geo || base._isGeo;

  return {
    autosize: true,
    paper_bgcolor: "#ffffff",
    plot_bgcolor:  isGeoChart ? "#f8fafc" : "#f9fafb",
    font: LIGHT_FONT,
    margin: isGeoChart
      ? { l: 0, r: 0, t: titleText ? 36 : 0, b: 0, pad: 0 }
      : { l: 80, r: 60, t: titleText ? 54 : 32, b: 120, pad: 4 },
    ...(titleText ? { title: { text: titleText, font: { ...LIGHT_FONT, size: 14, color: "#111827" } } } : {}),
    ...(base.barmode   ? { barmode:   base.barmode   } : {}),
    ...(base.barnorm   ? { barnorm:   base.barnorm   } : {}),
    ...(base.boxmode   ? { boxmode:   base.boxmode   } : {}),
    ...(base.violinmode? { violinmode:base.violinmode } : {}),
    hovermode: base.hovermode ?? "closest",
    xaxis:  { ...safeAxis(base.xaxis),  ...AXIS, tickangle: base.xaxis?.tickangle ?? -35 },
    yaxis:  { ...safeAxis(base.yaxis),  ...AXIS },
    ...(base.xaxis2 ? { xaxis2: { ...safeAxis(base.xaxis2), ...AXIS, tickangle: base.xaxis2?.tickangle ?? -35 } } : {}),
    ...(base.yaxis2 ? { yaxis2: { ...safeAxis(base.yaxis2), ...AXIS } } : {}),
    geo: {
      ...(base.geo?.projection ? { projection: base.geo.projection } : {}),
      ...(base.geo?.scope      ? { scope:      base.geo.scope      } : {}),
      ...(base.geo?.center     ? { center:     base.geo.center     } : {}),
      ...(base.geo?.lataxis    ? { lataxis:    base.geo.lataxis    } : {}),
      ...(base.geo?.lonaxis    ? { lonaxis:    base.geo.lonaxis    } : {}),
      bgcolor:        "#f8fafc",
      landcolor:      "#e2e8f0",
      oceancolor:     "#dbeafe",
      lakecolor:      "#dbeafe",
      rivercolor:     "#dbeafe",
      subunitcolor:   "rgba(0,0,0,0.1)",
      countrycolor:   "rgba(0,0,0,0.2)",
      coastlinecolor: "rgba(0,0,0,0.2)",
      showland:       true,
      showocean:      true,
      showlakes:      true,
      showrivers:     false,
      showcoastlines: true,
      showcountries:  true,
      showsubunits:   true,
      framecolor:     "rgba(0,0,0,0.1)",
      framewidth:     1,
    },
    legend: {
      orientation: "h" as const, y: -0.22, x: 0,
      bgcolor: "rgba(255,255,255,0)", bordercolor: "rgba(255,255,255,0)",
      font: LIGHT_FONT,
    },
    colorway: baseColorway.length
      ? baseColorway.map((c: string, i: number) => isDarkColor(c) ? LIGHT_PALETTE[i % LIGHT_PALETTE.length] : c)
      : LIGHT_PALETTE,
    hoverlabel: {
      bgcolor: "#ffffff", bordercolor: "rgba(0,0,0,0.15)",
      font: { ...LIGHT_FONT, color: "#111827" },
    },
    modebar: {
      bgcolor: "rgba(255,255,255,0)", color: "rgba(0,0,0,0.4)", activecolor: "#6366f1",
    },
  };
}

function isDarkColor(c: any): boolean {
  if (typeof c !== "string") return false;
  const lc = c.toLowerCase().trim();
  const hex = lc.startsWith("#") ? lc : null;
  if (hex) {
    const full = hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
    if (full.length === 7) {
      const r = parseInt(full.slice(1,3),16);
      const g = parseInt(full.slice(3,5),16);
      const b = parseInt(full.slice(5,7),16);
      return (0.299*r + 0.587*g + 0.114*b) / 255 < 0.3;
    }
  }
  return false;
}

function lightTrace(trace: any, idx: number): any {
  const fallback = LIGHT_PALETTE[idx % LIGHT_PALETTE.length];
  const t = { ...trace };
  if (t.marker) {
    t.marker = { ...t.marker };
    if (t.marker.color !== undefined) {
      t.marker.color = isDarkColor(t.marker.color) ? fallback : t.marker.color;
    }
    if (t.marker.line?.color && isDarkColor(t.marker.line.color)) {
      t.marker = { ...t.marker, line: { ...t.marker.line, color: "rgba(0,0,0,0.1)" } };
    }
  }
  if (t.line?.color && isDarkColor(t.line.color)) {
    t.line = { ...t.line, color: fallback };
  }
  if (t.fillcolor && isDarkColor(t.fillcolor)) {
    t.fillcolor = fallback + "20";
  }
  if ((t.type === "pie" || t.type === "sunburst") && t.marker?.colors) {
    t.marker = { ...t.marker, colors: t.marker.colors.map((c: any, i: number) => isDarkColor(c) ? LIGHT_PALETTE[i % LIGHT_PALETTE.length] : c) };
  }
  if (t.type === "pie" || t.type === "sunburst") {
    t.textfont = { ...(t.textfont ?? {}), color: "#111827" };
    t.outsidetextfont = { ...(t.outsidetextfont ?? {}), color: "#374151" };
  }
  if (t.textfont?.color && isDarkColor(t.textfont.color)) {
    t.textfont = { ...t.textfont, color: "#374151" };
  }
  return t;
}

// ─── InsightsPanel — tabbed All/Critical/Warnings/Success/Raw ────────────────

function InsightsPanel({ insights }: { insights: DomainInsight[] }) {
  const [tab, setTab] = useState<"all"|"critical"|"warning"|"success"|"raw">("all");
  const critical = insights.filter(i => i.severity === "critical");
  const warnings  = insights.filter(i => i.severity === "warning");
  const success   = insights.filter(i => i.severity === "success");

  const tabs: Array<{ id: typeof tab; label: string; count?: number }> = [
    { id: "all",      label: `All (${insights.length})` },
    { id: "critical", label: `Critical (${critical.length})` },
    { id: "warning",  label: `Warnings (${warnings.length})` },
    { id: "success",  label: `Success (${success.length})` },
    { id: "raw",      label: "Raw Data" },
  ];

  const tabBtn = (t: typeof tab): React.CSSProperties => ({
    padding: "7px 14px", border: "none",
    borderBottom: tab === t ? "2px solid var(--indigo)" : "2px solid transparent",
    background: "none", cursor: "pointer", fontSize: 13,
    fontWeight: tab === t ? 600 : 400,
    color: tab === t ? "var(--indigo)" : "var(--ink-soft)",
    whiteSpace: "nowrap",
  });

  function EmptyState({ msg }: { msg: string }) {
    return <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-soft)", fontSize: 14 }}>{msg}</div>;
  }

  const current = tab === "critical" ? critical : tab === "warning" ? warnings : tab === "success" ? success : insights;

  return (
    <div>
      <div style={{ display: "flex", borderBottom: "1px solid var(--line-soft)", overflowX: "auto" }}>
        {tabs.map(t => <button key={t.id} style={tabBtn(t.id)} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>
      <div style={{ paddingTop: 16 }}>
        {tab === "raw" ? (
          <pre style={{ fontSize: 11, overflowX: "auto", maxHeight: 400, background: "var(--paper-1)", padding: 16, borderRadius: 8, border: "1px solid var(--line-soft)" }}>
            {JSON.stringify(insights, null, 2)}
          </pre>
        ) : current.length === 0 ? (
          <EmptyState msg={tab === "critical" ? "✅ No critical issues found" : tab === "warning" ? "No warnings" : tab === "success" ? "No positives flagged — check All Insights" : "No insights available"} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {current.map((ins, i) => <InsightCard key={i} insight={ins} idx={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ChartsPanel ──────────────────────────────────────────────────────────────

function ChartsPanel({ charts }: { charts: any[] }) {
  const [openInsights, setOpenInsights] = useState<Record<string, boolean>>({});
  const isLight = useThemeColors();
  const isDark = !isLight;
  if (!charts.length) return <p style={{ padding: 32, textAlign: "center", color: "var(--ink-soft)" }}>No charts available</p>;
  const sorted = sortCharts(charts);

  const priorityBadge = (p?: string) => {
    if (!p) return null;
    const colors: Record<string, string> = { critical: "#ef4444", high: "#f97316", medium: "#eab308", low: "#22c55e" };
    return (
      <span style={{ padding: "1px 8px", borderRadius: 20, fontSize: 11, border: `1px solid ${colors[p] ?? "#ccc"}`, color: colors[p] ?? "#888", textTransform: "uppercase" }}>
        {p}
      </span>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {sorted.map((chart, i) => {
        const id = chart.id || String(i);
        const isWide = true;
        const hasData = chart.spec?.data && (!Array.isArray(chart.spec.data) || chart.spec.data.length > 0);
        const isGeoChart = hasData && (chart.spec.data as any[]).some((t: any) =>
          t.type === "choropleth" || t.type === "choroplethmap" || t.type === "scattergeo"
        );
        const plotHeight = isGeoChart ? 560 : 420;
        const insOpen = openInsights[id] || false;
        return (
          <div key={id} style={{ background: "var(--paper-1)", border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{chart.title}</p>
                  {priorityBadge(chart.priority)}
                </div>
                {chart.question && <p style={{ margin: 0, fontSize: 12, color: "var(--ink-soft)" }}><strong>Question:</strong> {chart.question}</p>}
              </div>
            </div>
            <div style={{ padding: "0 20px 4px" }}>
              {!hasData ? (
                <p style={{ fontSize: 13, color: "var(--ink-soft)", padding: "12px 0" }}>No data available for this chart</p>
              ) : (
                <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--line-strong)" }}>
                  <Plot
                    data={(chart.spec.data as any[]).map((t: any, i: number) => isDark ? darkTrace(t, i) : lightTrace(t, i))}
                    layout={cleanLayout(chart.spec.layout, isDark)}
                    config={{ responsive: true, displayModeBar: true, displaylogo: false, modeBarButtonsToRemove: ["pan2d","lasso2d","select2d"] as any }}
                    style={{ width: "100%", height: plotHeight }}
                    useResizeHandler
                  />
                </div>
              )}
            </div>
            {chart.insights && (
              <div style={{ padding: "0 20px 16px" }}>
                <button
                  onClick={() => setOpenInsights(prev => ({ ...prev, [id]: !insOpen }))}
                  style={{ width: "100%", padding: "8px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: 8, border: "1px solid var(--line-soft)", background: "none", cursor: "pointer", fontSize: 13 }}
                >
                  <span>💡 Chart Insights</span>
                  <ChevronIcon open={insOpen} />
                </button>
                {insOpen && (
                  <div style={{ marginTop: 8, padding: "12px 14px", borderRadius: 8, border: "1px solid var(--line-soft)", background: "var(--paper-2)", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                    {chart.insights}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function FullAnalysisResults({ analysisState, jobId }: Props) {
  const [activeTab, setActiveTab] = useState<"metrics" | "insights" | "charts">("metrics");
  const { profile, loading: authLoading } = useAuth();

  const isFreePlan = useMemo(() => {
    if (authLoading || !profile) return false;
    const sub = profile.subscription;
    const tier = sub?.tier ?? (sub?.plan === "pro_monthly" || sub?.plan === "pro_annual" ? "pro" : sub?.plan === "enterprise" ? "enterprise" : "free");
    return tier === "free";
  }, [profile, authLoading]);

  if (!analysisState.results) return null;

  const results        = analysisState.results;
  // Use analysis_id from the result payload, falling back to the jobId from the URL
  const downloadId     = results.analysis_id ?? jobId;
  const executiveSummary = extractExecutiveSummary(results);
  const metrics        = extractMetrics(results);
  const insights       = extractInsights(results);
  const charts         = extractCharts(results);

  const tabStyle = (t: string): React.CSSProperties => ({
    padding: "8px 18px", border: "none",
    borderBottom: activeTab === t ? "2px solid var(--indigo)" : "2px solid transparent",
    background: "none", cursor: "pointer", fontSize: 14,
    fontWeight: activeTab === t ? 600 : 400,
    color: activeTab === t ? "var(--indigo)" : "var(--ink-soft)",
    transition: "all 0.15s",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} id="analysis-results-container">

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Analysis Results</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {downloadId && (
            <button
              onClick={() => downloadPdf(downloadId)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, background: "none", border: "1px solid var(--line-soft)", cursor: "pointer", fontSize: 13 }}
            >
              <PdfIcon /> Download PDF
            </button>
          )}
          {downloadId && (
            <button
              onClick={() => downloadExcel(downloadId)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, background: "none", border: "1px solid var(--line-soft)", cursor: "pointer", fontSize: 13 }}
            >
              <SheetIcon /> Download Excel
            </button>
          )}
          <span style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, fontSize: 12, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", color: "#22c55e" }}>
            <CheckIcon /> Analysis Complete
          </span>
        </div>
      </div>

      {/* Executive Summary */}
      {executiveSummary && (
        <div style={{ padding: "16px 20px", background: "var(--paper-1)", border: "1px solid var(--line-soft)", borderLeft: "4px solid rgba(99,102,241,0.6)", borderRadius: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 14, fontWeight: 600 }}>
            <DocIcon /> Executive Summary
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-line" }}>{executiveSummary}</p>
        </div>
      )}

      {/* Tabs */}
      <div>
        <div style={{ display: "flex", borderBottom: "1px solid var(--line-soft)", overflowX: "auto" }}>
          <button style={tabStyle("metrics")}  onClick={() => setActiveTab("metrics")}>Metrics</button>
          <button style={tabStyle("insights")} onClick={() => setActiveTab("insights")}>
            Strategic Insights {insights.length > 0 && `(${insights.length})`}
          </button>
          <button style={tabStyle("charts")}   onClick={() => setActiveTab("charts")}>
            Visualizations {charts.length > 0 && `(${charts.length})`}
          </button>
        </div>

        <div style={{ paddingTop: 20 }}>
          {activeTab === "metrics" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {metrics && Object.keys(metrics).length > 0 ? (
                Object.entries(metrics).map(([name, data]) => (
                  <MetricSection key={name} name={name} data={data} analysisId={results.analysis_id} />
                ))
              ) : (
                <p style={{ textAlign: "center", color: "var(--ink-soft)", padding: 32 }}>No metrics available</p>
              )}
            </div>
          )}

          {activeTab === "insights" && (
            <div style={{ position: "relative", overflow: "hidden" }}>
              <div style={{ filter: isFreePlan ? "blur(3px)" : "none", pointerEvents: isFreePlan ? "none" : "auto", userSelect: isFreePlan ? "none" : "auto" }}>
                <InsightsPanel insights={insights} />
              </div>
              {isFreePlan && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.85)", zIndex: 10, border: "1px dashed #d1d5db", borderRadius: 12, margin: 0 }}>
                  <Link href="/pricing" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 24px", borderRadius: 10, background: "rgba(255,255,255,0.95)", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textDecoration: "none", cursor: "pointer" }}>
                    <Lock size={28} style={{ color: "#6366f1" }} />
                    <span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>Upgrade to Pro to view strategic insights</span>
                    <span style={{ display: "inline-block", padding: "6px 16px", fontSize: 12, fontWeight: 600, color: "#fff", borderRadius: 8, background: "linear-gradient(135deg, #5B6CFF, #EC4899)" }}>Upgrade to Pro</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {activeTab === "charts" && (
            <div style={{ position: "relative", overflow: "hidden" }}>
              <div style={{ filter: isFreePlan ? "blur(3px)" : "none", pointerEvents: isFreePlan ? "none" : "auto", userSelect: isFreePlan ? "none" : "auto" }}>
                <ChartsPanel charts={charts} />
              </div>
              {isFreePlan && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.85)", zIndex: 10, border: "1px dashed #d1d5db", borderRadius: 12, margin: 0 }}>
                  <Link href="/pricing" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 24px", borderRadius: 10, background: "rgba(255,255,255,0.95)", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textDecoration: "none", cursor: "pointer" }}>
                    <Lock size={28} style={{ color: "#6366f1" }} />
                    <span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>Upgrade to Pro to view visualizations</span>
                    <span style={{ display: "inline-block", padding: "6px 16px", fontSize: 12, fontWeight: 600, color: "#fff", borderRadius: 8, background: "linear-gradient(135deg, #5B6CFF, #EC4899)" }}>Upgrade to Pro</span>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function BarIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
}
function TrendIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
}
function ChevronIcon({ open }: { open: boolean }) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>;
}
function DocIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>;
}
function CheckIcon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>;
}
function SheetIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>;
}
function PdfIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>;
}
function SevIcon({ sev, size = 16 }: { sev: string; size?: number }) {
  const c = { critical: "#ef4444", warning: "#f97316", success: "#22c55e", info: "#6366f1" }[sev] ?? "#6366f1";
  if (sev === "critical" || sev === "warning") return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
  if (sev === "success") return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
}
